import React, { useEffect, useMemo, useState } from 'react';

import { Box, Chip, CircularProgress, Typography } from '@mui/material';

import Table from 'components/common/Table';
import IncidentReportService from 'services/incident-report.service';
import SnackbarNotify from 'utils/snackbar/SnackbarNotify';
import {
    errorMessageFrom,
    formatDate,
    incidentCategoryLabel,
    incidentSeverityLabel,
    incidentSubtypeDetail,
    severityColor
} from 'utils/helpers/guard-report.helper';
import IncidentReportDetailDialog from './IncidentReportDetailDialog';

// ==============================|| GUARD REPORTS - OCCUPANT VIOLATIONS ||============================== //

const MemberViolationsSection = ({ orgId, uid }) => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedId, setSelectedId] = useState(null);
    const [reloadToken, setReloadToken] = useState(0);

    useEffect(() => {
        if (!orgId || !uid) return undefined;

        let cancelled = false;
        setLoading(true);

        IncidentReportService.getViolations(orgId, uid)
            .then(data => {
                if (!cancelled) setRows(Array.isArray(data) ? data : []);
            })
            .catch(e => {
                if (cancelled) return;
                SnackbarNotify.error(`Failed to load violations - ${errorMessageFrom(e)}`);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [orgId, uid, reloadToken]);

    const columns = useMemo(
        () => [
            { Header: 'Report #', accessor: 'report_no' },
            {
                Header: 'Reported',
                accessor: 'reported_at',
                Cell: ({ value }) => formatDate(value)
            },
            {
                Header: 'Category',
                accessor: 'category',
                Cell: ({ row: { original } }) =>
                    `${incidentCategoryLabel(original.category)} · ${incidentSubtypeDetail(
                        original.sub_type,
                        original.sub_type_other
                    )}`
            },
            {
                Header: 'Severity',
                accessor: 'severity',
                Cell: ({ value }) => (
                    <Chip
                        size="small"
                        color={severityColor(value)}
                        label={incidentSeverityLabel(value)}
                    />
                )
            }
        ],
        []
    );

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={24} />
            </Box>
        );
    }

    if (rows.length === 0) {
        return (
            <Typography variant="body2" color="text.secondary">
                No violations.
            </Typography>
        );
    }

    return (
        <>
            <Table columns={columns} data={rows} onRowClick={row => setSelectedId(row.id)} />
            <IncidentReportDetailDialog
                orgId={orgId}
                incidentId={selectedId}
                onClose={() => setSelectedId(null)}
                onChanged={() => setReloadToken(t => t + 1)}
            />
        </>
    );
};

export default MemberViolationsSection;
